<template>
    <form ref="theForm" method="get" :action="generateFormRoute()" @submit="makeReport">
        <div class="row">
            <div class="col-md-4">
                <AdminBlock title="Period Start">
                    <div class="input-group">
                        <input ref="periodStartInput" type="text" placeholder="Select a date" class="form-control js-flatpickers" id="collection_start" name="collection_date" v-model="period_start" required />
                        <span class="input-group-text"><i class="fa fa-calendar"></i></span>
                    </div>
                </AdminBlock>
            </div>
            <div class="col-md-4">
                <AdminBlock title="Period End">
                    <div class="input-group">
                        <input ref="periodEndInput" type="text" placeholder="Select a date" class="form-control js-flatpickers" id="collection_end" name="delivery_date" v-model="period_end" required />
                        <span class="input-group-text"><i class="fa fa-calendar"></i></span>
                    </div>
                </AdminBlock>
            </div>
            <div class="col-md-4">
                <AdminBlock title="Driver">
                    <DriverSelect
                        no-label
                        remove-margin
                    />
                </AdminBlock>
            </div>
        </div>

        <AdminBlock>
            <button type="button" class="btn btn-sm btn-primary w-100 mb-2"
                    @click="makeReport($event, 'preview')"
                    name="output"
                    value="preview"
                    :disabled="isPreviewLoading"
                    :aria-busy="isPreviewLoading"
            >
                {{ isPreviewLoading ? 'Loading…' : 'Preview' }}
            </button>
            <button type="submit"
                    class="btn btn-sm btn-secondary w-100"
                    name="output"
                    value="download"
                    :disabled="isDownloadLoading"
            >
                {{ isDownloadLoading ? 'Please wait' : 'Download' }}
            </button>
        </AdminBlock>

        <AdminBlock title="Preview">
            <iframe
                ref="reportPreview"
                v-if="reportPreviewSrc"
                :src="reportPreviewSrc"
                class="w-100"
                :class="{ 'mt-4 border': previewReady }"
                :style="{ 'height': previewReady ? '500px' : '100px' }"
                @load="onPreviewLoad"
            />
        </AdminBlock>

    </form>
</template>

<script setup>

import Swal from "sweetalert2";
import { ref, onMounted, nextTick } from "vue";
import AdminBlock from "@/components/movements/components/AdminBlock.vue";
import DriverSelect from "@/components/movements/components/DriverSelect.vue";
import flatpickr from 'flatpickr';
import { DateTime } from 'luxon';


const theForm = ref();
const period_start = ref("");
const period_end = ref("");
const periodStartInput = ref();
const periodEndInput = ref();

const reportPreview = ref();
const isPreviewLoading = ref(false);
const reportPreviewSrc = ref("");
const previewReady = ref(false);

const generateFormRoute = () => {
    return route('movements.reports.driver-movements.export')
}

const onPreviewLoad = () => {
    isPreviewLoading.value = false;
    previewReady.value = true;
};

const makeReport = function(ev, output) {
    output = output || 'download';

    if (output === 'download') {
        // Don't preventDefault, let the form submit
        return;
    }

    ev.preventDefault();

    if (! theForm.value.checkValidity()) {
        Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'error',
            title: 'Error Generating Price',
            text: 'Finish the form please',
            width: '400px',
            confirmButtonText: 'Dismiss',
            confirmButtonColor: '#dc3545 ',
        });
        return;
    }

    isPreviewLoading.value = true;

    const data = new FormData(theForm.value);

    if (! data.has('output')) {
        data.append('output', output);
    }

    const queryString = new URLSearchParams();

    for (const key of data.keys()) {
        queryString.append(key, data.get(key));
    }

    const newValue = `${generateFormRoute()}?${queryString.toString()}`;

    // if the value doesn't change then the load event is never fired and the button is disabled forever
    if (reportPreviewSrc.value !== newValue) {
        reportPreviewSrc.value = newValue;
    } else {
        // add a little delay so the UI looks like its changing even though it isn't
        setTimeout(() => {
            isPreviewLoading.value = false;
        }, 100);
    }
};

const initialiseFlatpickr = () => {
    flatpickr('.js-flatpickers', {
        dateFormat: "Y-m-d",
        enableTime: false,
        altInput: true,
    });
};

onMounted(() => {
    nextTick(() => {
        initialiseFlatpickr();
        const today = DateTime.now();
        // Previous Friday (if today is Friday, use today)
        const prevFriday = today.weekday >= 5 ? today.set({ weekday: 5 }) : today.minus({ weeks: 1 }).set({ weekday: 5 });
        // Next Friday (if today is Friday, use today)
        const nextFriday = today.weekday <= 5 ? today.set({ weekday: 5 }) : today.plus({ weeks: 1 }).set({ weekday: 5 });
        period_start.value = prevFriday.toFormat("yyyy-MM-dd");
        period_end.value = nextFriday.toFormat("yyyy-MM-dd");
        periodStartInput.value._flatpickr.setDate(period_start.value);
        periodEndInput.value._flatpickr.setDate(period_end.value);
    });
});
</script>
